import { ChainId, Currency } from '@pancakeswap/sdk'
import { BinanceIcon, TokenLogo } from '@pancakeswap/uikit'
import { useMemo } from 'react'
import { WrappedTokenInfo } from '@pancakeswap/token-lists'
import styled from 'styled-components'
import { ASSET_CDN } from 'config/constants/endpoints'
import { useHttpLocations } from '@pancakeswap/hooks'
import { CAD1_PUBLIC_ENV, cad1RegistryAddresses, resolveCad1Config } from 'views/Cad1/config'
import { useProjectInfo } from 'views/TokenPages/hooks'
import getTokenLogoURL from '../../utils/getTokenLogoURL'

/** A launcher is configured at build time (production has none yet: no CAD1 request on any page). */
const CAD1_ON = cad1RegistryAddresses(resolveCad1Config(CAD1_PUBLIC_ENV)).length > 0

const StyledLogo = styled(TokenLogo)<{ size: string }>`
  width: ${({ size }) => size};
  height: ${({ size }) => size};
  border-radius: 50%;
`

interface LogoProps {
  currency?: Currency
  size?: string
  style?: React.CSSProperties
}

export function FiatLogo({ currency, size = '24px', style }: LogoProps) {
  return <StyledLogo size={size} srcs={[`/${currency?.symbol}.svg`]} width={size} style={style} />
}

export default function CurrencyLogo({ currency, size = '24px', style }: LogoProps) {
  const uriLocations = useHttpLocations(currency instanceof WrappedTokenInfo ? currency.logoURI : undefined)
  // a CAD1 launch's logo is published by the token-pages API, bound by the token's on-chain hash; only …cad1 addresses are asked
  const cad1Address = CAD1_ON && currency?.isToken && /cad1$/i.test(currency.address) ? currency.address : undefined
  const cad1Info = useProjectInfo(cad1Address).data
  const cad1Logo = cad1Info?.info?.logoUrl ?? cad1Info?.cad1Info?.logoUrl ?? cad1Info?.basicInfo?.logoUrl ?? null

  const srcs: string[] = useMemo(() => {
    if (currency?.isNative) return []

    if (currency?.isToken) {
      const tokenLogoURL = getTokenLogoURL(currency)
      const own = cad1Logo ? [cad1Logo] : []

      if (currency instanceof WrappedTokenInfo) {
        if (!tokenLogoURL) return [...own, ...uriLocations]
        return [...own, ...uriLocations, tokenLogoURL]
      }
      if (!tokenLogoURL) return own
      return [...own, tokenLogoURL]
    }
    return []
  }, [currency, uriLocations, cad1Logo])

  if (currency?.isNative) {
    if (currency.chainId === ChainId.BSC) {
      return <BinanceIcon width={size} style={style} />
    }
    return (
      <StyledLogo size={size} srcs={[`${ASSET_CDN}/web/native/${currency.chainId}.png`]} width={size} style={style} />
    )
  }

  return <StyledLogo size={size} srcs={srcs} alt={`${currency?.symbol ?? 'token'} logo`} style={style} />
}
